import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CmsFields } from './SlotEditor';
import { CollapsibleItemCard, ExpandCollapseAll } from './CollapsibleItemCard';
import { dropOpen, itemSummary, swapOpen } from './listCollapse';
import type { CmsBlockSchema, CmsContentValue } from '../../lib/api/cms';

type Block = Record<string, unknown> & { type: string };

interface CmsBlockEditorProps {
  blockSchemas: Record<string, CmsBlockSchema>;
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
}

export const CmsBlockEditor: React.FC<CmsBlockEditorProps> = ({ blockSchemas, blocks, onChange }) => {
  const [addType, setAddType] = useState<string>('');
  // Blöcke starten zugeklappt; ein neu angelegter Block geht direkt auf.
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set<number>());
  const types = Object.values(blockSchemas);

  const toggle = (i: number, isOpen: boolean) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(i);
      else next.delete(i);
      return next;
    });

  const update = (i: number, next: CmsContentValue) =>
    onChange(blocks.map((b, idx) => (idx === i ? ({ ...next, type: b.type } as Block) : b)));
  const remove = (i: number) => {
    setOpen((prev) => dropOpen(prev, i));
    onChange(blocks.filter((_, idx) => idx !== i));
  };
  const move = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[target]] = [next[target], next[i]];
    setOpen((prev) => swapOpen(prev, i, target));
    onChange(next);
  };
  const add = () => {
    if (!addType) return;
    setOpen((prev) => new Set(prev).add(blocks.length));
    onChange([...blocks, { type: addType } as Block]);
    setAddType('');
  };

  const allOpen = blocks.length > 0 && open.size >= blocks.length;

  return (
    <div className="space-y-4">
      {blocks.length === 0 && (
        <p className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
          Noch keine Blöcke. Füge unten den ersten hinzu.
        </p>
      )}

      {blocks.length > 1 && (
        <div className="flex justify-end">
          <ExpandCollapseAll
            allOpen={allOpen}
            onToggle={() => setOpen(allOpen ? new Set<number>() : new Set(blocks.map((_, i) => i)))}
          />
        </div>
      )}

      {blocks.map((block, i) => {
        const spec = blockSchemas[block.type];
        return (
          <CollapsibleItemCard
            key={i}
            index={i}
            count={blocks.length}
            title={spec?.label ?? block.type}
            summary={spec ? itemSummary(block, spec.fields) : ''}
            open={open.has(i)}
            onOpenChange={(isOpen) => toggle(i, isOpen)}
            onMove={(dir) => move(i, dir)}
            onRemove={() => remove(i)}
            removeLabel="Block entfernen"
          >
            {spec ? (
              <CmsFields fields={spec.fields} value={block} onChange={(v) => update(i, v)} />
            ) : (
              <p className="text-sm text-muted-foreground">Unbekannter Block-Typ „{block.type}".</p>
            )}
          </CollapsibleItemCard>
        );
      })}

      <div className="flex gap-2">
        <Select value={addType || undefined} onValueChange={setAddType}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Block-Typ wählen…" />
          </SelectTrigger>
          <SelectContent>
            {types.map((t) => (
              <SelectItem key={t.type} value={t.type}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" onClick={add} disabled={!addType}>
          <Plus className="mr-2 h-4 w-4" /> Block hinzufügen
        </Button>
      </div>
    </div>
  );
};

export default CmsBlockEditor;
